import { Dispatch, SetStateAction } from 'react';
import { CategoryType } from 'src/graphql';

type CategoryPropertyKey =
  | 'isDeterminedCategory'
  | 'isReportedCategory'
  | 'displayInOpenReporting'
  | 'displayInApp'
  | 'allowGoTogether'
  | 'question'
  | 'displayInVaultTalk'
  | 'displayInCollaborationToolIntegration'
  | 'displayInManualIntake';

export const toggleCategoryProperty = (
  setCurrentCategory: Dispatch<SetStateAction<CategoryType | null>>,
  key: CategoryPropertyKey,
) => {
  setCurrentCategory((cat) => (cat ? { ...cat, [key]: !cat[key] } : cat));
};

export const setCategoryQuestion = (
  setCurrentCategory: Dispatch<SetStateAction<CategoryType | null>>,
  question: boolean,
) => {
  setCurrentCategory((cat) => (cat ? { ...cat, question } : cat));
};

export const toggleReportedCategoryOrQuestion = (
  setCurrentCategory: Dispatch<SetStateAction<CategoryType | null>>,
  vaultTalkEnabled?: boolean,
  oprEnabled?: boolean,
  goTogetherEnabled?: boolean,
  slackEnabled?: boolean,
  key?: CategoryPropertyKey,
) => {
  setCurrentCategory((cat) => {
    if (!cat) {
      return cat;
    }
    const questionToggle = key === 'question';
    if (cat.isReportedCategory === true && !questionToggle) {
      // turn off all intake methods if turning off reportedCategory
      return {
        ...cat,
        isReportedCategory: false,
        displayInManualIntake: false,
        displayInApp: false,
        displayInOpenReporting: false,
        displayInVaultTalk: false,
        displayInCollaborationToolIntegration: false,
        allowGoTogether: false,
        question: false,
      };
    } else if (cat.isReportedCategory === false) {
      const generalQueryToggle = questionToggle ? { question: true } : {};

      // turn on all intake methods if turning on reportedCategory or
      // turn on all intake methods if turning on question if all intake methods are off
      return {
        ...cat,
        ...generalQueryToggle,
        isReportedCategory: true,
        displayInManualIntake: true,
        displayInApp: true,
        displayInOpenReporting: oprEnabled ? true : false,
        displayInVaultTalk: vaultTalkEnabled ? true : false,
        displayInCollaborationToolIntegration: slackEnabled ? true : false,
        allowGoTogether: goTogetherEnabled ? true : false,
      };
    }
    return {
      ...cat,
      question: !cat['question'],
    };
  });
};

export const toggleIntakeMethod = (
  setCurrentCategory: Dispatch<SetStateAction<CategoryType | null>>,
  intakeMethod: CategoryPropertyKey,
) => {
  setCurrentCategory((cat) => {
    if (!cat) {
      return cat;
    }
    // turn off allowGoTogether if turning off displayInApp
    const onDisplayInAppToggle =
      intakeMethod === 'displayInApp'
        ? {
            allowGoTogether:
              cat?.displayInApp === true ? false : !!cat.allowGoTogether,
          }
        : {};
    // turn on displayInApp if turning on allowGoTogether
    const onAllowGoTogetherToggle =
      intakeMethod === 'allowGoTogether'
        ? {
            displayInApp:
              cat?.allowGoTogether === false ? true : !!cat.displayInApp,
          }
        : {};

    const allIntakeMethods: { [key: string]: boolean } = {
      displayInManualIntake: cat?.displayInManualIntake,
      displayInOpenReporting: cat?.displayInOpenReporting,
      displayInApp: cat?.displayInApp,
      displayInVaultTalk: cat?.displayInVaultTalk,
      displayInCollaborationToolIntegration:
        cat?.displayInCollaborationToolIntegration,
    };
    allIntakeMethods[intakeMethod] = !cat[intakeMethod];

    const allIntakeMethodsOff = Object.values(allIntakeMethods).every(
      (value) => !value,
    );
    const reportedCategoryToggle = {
      isReportedCategory: !allIntakeMethodsOff,
    };

    const generalQueryToggle = allIntakeMethodsOff ? { question: false } : {};

    return {
      ...cat,
      [intakeMethod]: !cat[intakeMethod],
      ...reportedCategoryToggle,
      ...onDisplayInAppToggle,
      ...onAllowGoTogetherToggle,
      ...generalQueryToggle,
    };
  });
};
